import React, { useState } from 'react'
import { DocumentCheckIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

const Validator: React.FC = () => {
  const [fileContent, setFileContent] = useState('')
  const [validationResults, setValidationResults] = useState<{
    isValid: boolean
    errors: string[]
    warnings: string[]
  } | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFileContent(e.target.value)
    setValidationResults(null)
  }

  const validateLlmstxt = () => {
    const errors: string[] = []
    const warnings: string[] = []

    // Check if file is empty
    if (!fileContent.trim()) {
      errors.push('File is empty')
      setValidationResults({ isValid: false, errors, warnings })
      return
    }

    // Check for required fields
    const requiredFields = ['Website:', 'Language:', 'Description:', 'Allow Training:', 'Allow Crawling:', 'Contact:']
    requiredFields.forEach(field => {
      if (!fileContent.includes(field)) {
        errors.push(`Missing required field: ${field}`)
      }
    })

    // Check for valid URL format
    const urlMatch = fileContent.match(/Website:\s*(https?:\/\/[^\s]+)/)
    if (urlMatch) {
      try {
        new URL(urlMatch[1])
      } catch {
        errors.push('Invalid website URL format')
      }
    }

    // Check for valid email format
    const emailMatch = fileContent.match(/Contact:\s*([^\s]+@[^\s]+\.[^\s]+)/)
    if (emailMatch) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(emailMatch[1])) {
        errors.push('Invalid contact email format')
      }
    }

    // Check for valid language code
    const languageMatch = fileContent.match(/Language:\s*([a-z]{2})/)
    if (languageMatch) {
      const validLanguages = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'zh', 'ja', 'ko']
      if (!validLanguages.includes(languageMatch[1])) {
        warnings.push('Uncommon language code detected')
      }
    }

    // Check for valid training/crawling values
    const trainingMatch = fileContent.match(/Allow Training:\s*(yes|no|partial)/i)
    const crawlingMatch = fileContent.match(/Allow Crawling:\s*(yes|no|partial)/i)
    
    if (trainingMatch && !['yes', 'no', 'partial'].includes(trainingMatch[1].toLowerCase())) {
      errors.push('Invalid value for Allow Training (must be yes, no, or partial)')
    }
    if (crawlingMatch && !['yes', 'no', 'partial'].includes(crawlingMatch[1].toLowerCase())) {
      errors.push('Invalid value for Allow Crawling (must be yes, no, or partial)')
    }

    // Check for reasonable description length
    const descriptionMatch = fileContent.match(/Description:\s*([^\n]+)/)
    if (descriptionMatch) {
      const description = descriptionMatch[1].trim()
      if (description.length < 10) {
        warnings.push('Description seems too short')
      } else if (description.length > 500) {
        warnings.push('Description seems too long')
      }
    }

    setValidationResults({
      isValid: errors.length === 0,
      errors,
      warnings
    })
  }

  return (
    <main className="py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold tracking-tight text-brand-black sm:text-4xl font-['Arial']">
              llms.txt Validator
            </h1>
            <p className="mt-4 text-lg text-neutral-400">
              Validate your llms.txt file for errors and best practices using our free validator tool
            </p>
          </div>

          <div className="bg-surface-light border border-neutral-200 rounded-2xl p-4 sm:p-8">
            <div className="space-y-6 sm:space-y-8">
              <div>
                <label htmlFor="fileContent" className="block text-sm font-medium text-brand-black">
                  Paste your llms.txt content
                </label>
                <textarea
                  id="fileContent"
                  rows={10}
                  value={fileContent}
                  onChange={handleFileChange}
                  className="mt-1 block w-full rounded-md bg-surface-light border-neutral-200 text-brand-black shadow-sm focus:border-brand-black focus:ring-brand-black/10 sm:text-sm font-mono"
                  placeholder="# Paste your llms.txt content here..."
                />
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={validateLlmstxt}
                  className="inline-flex items-center gap-x-2 px-4 py-2.5 text-sm font-semibold shadow-sm btn-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  <DocumentCheckIcon className="h-5 w-5" aria-hidden="true" />
                  Validate File
                </button>
              </div>

              {validationResults && (
                <div className="mt-8">
                  <div className={`rounded-lg p-4 ${validationResults.isValid ? 'bg-surface-muted' : 'bg-red-500/10'}`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        {validationResults.isValid ? (
                          <CheckCircleIcon className="h-5 w-5 text-brand-black" aria-hidden="true" />
                        ) : (
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                        )}
                      </div>
                      <div className="ml-3">
                        <h3 className={`text-sm font-medium ${validationResults.isValid ? 'text-brand-black' : 'text-red-500'}`}>
                          {validationResults.isValid ? 'File is valid' : 'File has errors'}
                        </h3>
                        {validationResults.errors.length > 0 && (
                          <div className="mt-2 text-sm text-red-400">
                            <ul className="list-disc space-y-1 pl-5">
                              {validationResults.errors.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {validationResults.warnings.length > 0 && (
                          <div className="mt-2 text-sm text-yellow-400">
                            <ul className="list-disc space-y-1 pl-5">
                              {validationResults.warnings.map((warning, index) => (
                                <li key={index}>{warning}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

export default Validator 